import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const Dashboard = () => {
  const [stockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    analysisType: 'product-wise',
    dateRange: 'all',
    productGroup: 'all',
    region: 'all',
    stockist: 'all'
  });
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStockData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stockData.length > 0) {
      applyFilters();
    }
  }, [stockData, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStockData = () => {
    try {
      const data = JSON.parse(localStorage.getItem('stockData') || '[]');
      setStockData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading stock data:', error);
      setStockData([]);
      setLoading(false);
    }
  };



  const applyFilters = () => {
    let filtered = [...stockData];
    
    if (filters.productGroup !== 'all') {
      filtered = filtered.filter(item => 
        item['Product Group'] === filters.productGroup
      );
    }
    
    if (filters.region !== 'all') {
      filtered = filtered.filter(item => item.Region === filters.region);
    }
    
    if (filters.stockist !== 'all') {
      filtered = filtered.filter(item => item.Stockist === filters.stockist);
    }
    
    setFilteredData(filtered);
    generateAnalytics(filtered);
  };

  const generateAnalytics = (data) => {
    const analytics = {};
    
    // Month-wise analysis
    analytics.monthWise = data.reduce((acc, item) => {
      const month = item.Month || 'Unknown';
      if (!acc[month]) {
        acc[month] = {
          totalSales: 0,
          totalQuantity: 0,
          primarySales: 0,
          secondarySales: 0,
          count: 0
        };
      }
      acc[month].totalSales += parseFloat(item['CLOSING VALUE'] || 0);
      acc[month].totalQuantity += parseInt(item['Sales Qty'] || 0);
      acc[month].primarySales += parseFloat(item['Primary Sales'] || 0);
      acc[month].secondarySales += parseFloat(item['Secondary Sales'] || 0);
      acc[month].count++;
      return acc;
    }, {});

    // Product-wise analysis
    analytics.productWise = data.reduce((acc, item) => {
      const product = item['PRODUCT DESCRIPTION'] || 'Unknown';
      if (!acc[product]) {
        acc[product] = {
          totalSales: 0,
          openingStock: 0,
          closingStock: 0,
          count: 0
        };
      }
      acc[product].totalSales += parseFloat(item['CLOSING VALUE'] || 0);
      acc[product].openingStock += parseInt(item['OPENING STOCK'] || 0);
      acc[product].closingStock += parseInt(item['CLOSING STOCK'] || 0);
      acc[product].count++;
      return acc;
    }, {});

    // Region-wise analysis
    analytics.regionWise = data.reduce((acc, item) => {
      const region = item.Region || 'Unknown';
      if (!acc[region]) {
        acc[region] = {
          totalSales: 0,
          totalQuantity: 0,
          count: 0
        };
      }
      acc[region].totalSales += parseFloat(item['CLOSING VALUE'] || 0);
      acc[region].totalQuantity += parseInt(item['Sales Qty'] || 0);
      acc[region].count++;
      return acc;
    }, {});

    // Stockist-wise analysis
    analytics.stockistWise = data.reduce((acc, item) => {
      const stockist = item.Stockist || 'Unknown';
      if (!acc[stockist]) {
        acc[stockist] = {
          totalSales: 0,
          totalQuantity: 0,
          primarySales: 0,
          secondarySales: 0,
          count: 0
        };
      }
      acc[stockist].totalSales += parseFloat(item['CLOSING VALUE'] || 0);
      acc[stockist].totalQuantity += parseInt(item['Sales Qty'] || 0);
      acc[stockist].primarySales += parseFloat(item['Primary Sales'] || 0);
      acc[stockist].secondarySales += parseFloat(item['Secondary Sales'] || 0);
      acc[stockist].count++;
      return acc;
    }, {});

    // Product Group-wise analysis
    analytics.productGroupWise = data.reduce((acc, item) => {
      const group = item['Product Group'] || 'Unknown';
      if (!acc[group]) {
        acc[group] = {
          totalSales: 0,
          openingStock: 0,
          closingStock: 0,
          primarySales: 0,
          secondarySales: 0,
          count: 0
        };
      }
      acc[group].totalSales += parseFloat(item['CLOSING VALUE'] || 0);
      acc[group].openingStock += parseInt(item['OPENING STOCK'] || 0);
      acc[group].closingStock += parseInt(item['CLOSING STOCK'] || 0);
      acc[group].primarySales += parseFloat(item['Primary Sales'] || 0);
      acc[group].secondarySales += parseFloat(item['Secondary Sales'] || 0);
      acc[group].count++;
      return acc;
    }, {});

    setAnalytics(analytics);
  };

  const getChartData = () => {
    switch (filters.analysisType) {
      case 'product-wise':
        return {
          labels: Object.keys(analytics.productWise || {}).slice(0, 10),
          datasets: [
            {
              label: 'Total Sales Value',
              data: Object.values(analytics.productWise || {}).slice(0, 10).map(item => item.totalSales),
              backgroundColor: [
                '#FF6384', '#36A2EB', '#FFCE56', '#FF9F40', '#FF6384',
                '#C9CBCF', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384'
              ]
            }
          ]
        };
      case 'region-wise':
        return {
          labels: Object.keys(analytics.regionWise || {}),
          datasets: [
            {
              label: 'Total Sales Value',
              data: Object.values(analytics.regionWise || {}).map(item => item.totalSales),
              backgroundColor: 'rgba(72, 187, 120, 0.6)',
              borderColor: 'rgba(72, 187, 120, 1)',
              borderWidth: 2
            }
          ]
        };
      default:
        return { labels: [], datasets: [] };
    }
  };


  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    
    // Main data sheet
    const ws1 = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Stock Data');
    
    // Analytics sheets
    if (analytics.monthWise) {
      const monthData = Object.entries(analytics.monthWise).map(([month, data]) => ({
        Month: month,
        'Total Sales': data.totalSales,
        'Total Quantity': data.totalQuantity,
        'Primary Sales': data.primarySales,
        'Secondary Sales': data.secondarySales
      }));
      const ws2 = XLSX.utils.json_to_sheet(monthData);
      XLSX.utils.book_append_sheet(wb, ws2, 'Month-wise Analysis');
    }
    
    XLSX.writeFile(wb, 'Stock_Analysis_Report.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.text('Stock Analysis Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    doc.text(`Total Records: ${filteredData.length}`, 20, 40);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 50);
    
    // Month-wise table
    if (analytics.monthWise) {
      const monthData = Object.entries(analytics.monthWise).map(([month, data]) => [
        month,
        data.totalSales.toFixed(2),
        data.totalQuantity.toString(),
        data.primarySales.toFixed(2),
        data.secondarySales.toFixed(2)
      ]);
      
      doc.autoTable({
        startY: 70,
        head: [['Month', 'Total Sales', 'Total Quantity', 'Primary Sales', 'Secondary Sales']],
        body: monthData,
        theme: 'grid'
      });
    }
    
    doc.save('Stock_Analysis_Report.pdf');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (stockData.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="empty-state">
          <div className="empty-state-content">
            <div className="empty-state-icon">📊</div>
            <h2>No Data Available</h2>
            <p>Please upload Excel or PDF files to view analytics and reports.</p>
            <div className="empty-state-actions">
              <button 
                className="upload-redirect-btn"
                onClick={() => window.location.href = '/upload'}
              >
                📁 Go to Upload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Analytics Dashboard</h1>
          <p className="data-source-info">
            📊 Analyzing data from uploaded files ({stockData.length} records)
          </p>
        </div>
        <div className="export-buttons">
          <button className="export-btn excel" onClick={exportToExcel}>
            📊 Export to Excel
          </button>
       
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Analysis Type:</label>
          <select 
            value={filters.analysisType} 
            onChange={(e) => setFilters({...filters, analysisType: e.target.value})}
          >
            <option value="product-wise">Product-wise</option>
            <option value="region-wise">Region-wise</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Product Group:</label>
          <select 
            value={filters.productGroup} 
            onChange={(e) => setFilters({...filters, productGroup: e.target.value})}
          >
            <option value="all">All Products</option>
            {[...new Set(stockData.map(item => item['Product Group']).filter(Boolean))].map(group => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Region:</label>
          <select 
            value={filters.region} 
            onChange={(e) => setFilters({...filters, region: e.target.value})}
          >
            <option value="all">All Regions</option>
            {[...new Set(stockData.map(item => item.Region).filter(Boolean))].map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Stockist:</label>
          <select 
            value={filters.stockist} 
            onChange={(e) => setFilters({...filters, stockist: e.target.value})}
          >
            <option value="all">All Stockists</option>
            {[...new Set(stockData.map(item => item.Stockist).filter(Boolean))].map(stockist => (
              <option key={stockist} value={stockist}>{stockist}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <h3>Total Records</h3>
          <div className="metric-value">{filteredData.length}</div>
        </div>
        <div className="metric-card">
          <h3>Total Sales Value</h3>
          <div className="metric-value">
            ₹{filteredData.reduce((sum, item) => sum + parseFloat(item['CLOSING VALUE'] || 0), 0).toFixed(2)}
          </div>
        </div>
        <div className="metric-card">
          <h3>Total Stock</h3>
          <div className="metric-value">
            {filteredData.reduce((sum, item) => sum + parseInt(item['CLOSING STOCK'] || 0), 0)}
          </div>
        </div>
        <div className="metric-card">
          <h3>Active Products</h3>
          <div className="metric-value">
            {new Set(filteredData.map(item => item['PRODUCT DESCRIPTION'])).size}
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>{filters.analysisType.replace('-', ' ').toUpperCase()} Analysis</h3>
          <div className="chart-container">
            {filters.analysisType === 'product-wise' || filters.analysisType === 'product-group-wise' ? (
              <Pie data={getChartData()} options={{ responsive: true, maintainAspectRatio: false }} />
            ) : (
              <Bar data={getChartData()} options={{ responsive: true, maintainAspectRatio: false }} />
            )}
          </div>
        </div>

      </div>

      <div className="data-table-section">
        <h3>Stock Data Summary</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product Code</th>
                <th>Product Description</th>
                <th>Opening Stock</th>
                <th>Closing Stock</th>
                <th>Sales Value</th>
                <th>Region</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.slice(0, 10).map((item, index) => (
                <tr key={index}>
                  <td>{item['PRODUCT CODE']}</td>
                  <td>{item['PRODUCT DESCRIPTION']}</td>
                  <td>{item['OPENING STOCK']}</td>
                  <td>{item['CLOSING STOCK']}</td>
                  <td>₹{parseFloat(item['CLOSING VALUE'] || 0).toFixed(2)}</td>
                  <td>{item.Region}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredData.length > 10 && (
            <div className="table-pagination">
              Showing 10 of {filteredData.length} records
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;